import { useEffect, useState } from "react";
import { t } from "../i18n";
import { Users, Phone, MessageCircle, Gift, Plane, PartyPopper } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "../lib/api";
import { Sheet } from "./Sheet";
import { PersonPicker, PlaceInput } from "./Pickers";
import { TIPOS, MONEDAS, toLocalInput, fromLocalInput } from "../lib/data";

export const TIPO_ICON = { encuentro: Users, llamada: Phone, mensaje: MessageCircle, regalo: Gift, viaje: Plane, evento: PartyPopper };

const blank = (init = {}) => ({
  person_ids: init.person_ids || [], tipo: init.tipo || "encuentro", fecha: toLocalInput(init.fecha, false),
  fecha_fin: init.fecha_fin ? toLocalInput(init.fecha_fin, false) : "", lugar: init.lugar || { nombre: "", ciudad: "", pais: "" },
  notas: init.notas || "", monto: init.monto ?? "", moneda: init.moneda || "MXN", nombre_evento: init.nombre_evento || "",
});

export function TipoSegment({ value, onChange }) {
  return (
    <div className="grid grid-cols-6 gap-1 bg-secondary rounded-xl p-1" data-testid="tipo-segment">
      {TIPOS.map((tp) => { const Icon = TIPO_ICON[tp.value]; return (
        <button type="button" key={tp.value} data-testid={`tipo-${tp.value}`} onClick={() => onChange(tp.value)}
          className={`flex flex-col items-center gap-1 py-2 rounded-lg text-[10px] transition-colors ${value === tp.value ? "bg-[#d4af37] text-[#0b0c0e]" : "text-muted-foreground hover:text-foreground"}`}>
          <Icon size={16} /> {tp.label}
        </button>); })}
    </div>
  );
}

export function InteractionSheet({ open, onClose, initial, onSaved }) {
  const [f, setF] = useState(blank());
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setF(blank(initial || {})); }, [open, initial]);

  const submit = async (e) => {
    e.preventDefault();
    if (f.person_ids.length === 0) return toast.error(t("interaction.selectPerson"));
    if (f.tipo === "viaje" && !f.fecha_fin) return toast.error(t("interaction.needEnd"));
    if (f.tipo === "evento" && !f.nombre_evento.trim()) return toast.error(t("interaction.needEvent"));
    setSaving(true);
    const payload = {
      person_ids: f.person_ids, tipo: f.tipo, fecha: fromLocalInput(f.fecha + "T12:00"),
      fecha_fin: f.tipo === "viaje" ? fromLocalInput(f.fecha_fin + "T12:00") : null,
      lugar: { nombre: f.lugar.nombre || null, ciudad: f.lugar.ciudad || null, pais: f.lugar.pais || null, lat: null, lng: null },
      notas: f.notas || null, monto: f.monto === "" ? null : Number(f.monto), moneda: f.moneda, nombre_evento: f.nombre_evento || null,
    };
    try {
      const { data } = initial?.id ? await api.put(`/interactions/${initial.id}`, payload) : await api.post("/interactions", payload);
      toast.success(initial?.id ? t("interaction.updated") : t("interaction.saved"));
      onSaved?.(data); onClose();
    } catch (err) { toast.error(errorMessage(err)); } finally { setSaving(false); }
  };

  const showMonto = ["regalo", "viaje", "encuentro"].includes(f.tipo);
  return (
    <Sheet open={open} onClose={onClose} title={initial?.id ? t("interaction.edit") : t("interaction.new")} testId="interaction-sheet">
      <form onSubmit={submit} className="space-y-4" data-testid="interaction-form">
        <PersonPicker value={f.person_ids} onChange={(v) => setF({ ...f, person_ids: v })} />
        <div><label className="label">{t("common.type")}</label><TipoSegment value={f.tipo} onChange={(v) => setF({ ...f, tipo: v })} /></div>
        <div className={`grid gap-2 ${f.tipo === "viaje" ? "grid-cols-2" : "grid-cols-1"}`}>
          <div><label className="label">{t("common.date")}</label><input data-testid="interaction-fecha" type="date" className="field" value={f.fecha} onChange={(e) => setF({ ...f, fecha: e.target.value })} required /></div>
          {f.tipo === "viaje" && <div><label className="label">{t("interaction.endDate")}</label><input data-testid="interaction-fecha-fin" type="date" className="field" min={f.fecha} value={f.fecha_fin} onChange={(e) => setF({ ...f, fecha_fin: e.target.value })} required /></div>}
        </div>
        {f.tipo === "evento" && <div><label className="label">{t("interaction.eventName")}</label><input data-testid="interaction-nombre-evento" className="field" value={f.nombre_evento} onChange={(e) => setF({ ...f, nombre_evento: e.target.value })} required /></div>}
        <PlaceInput value={f.lugar} onChange={(v) => setF({ ...f, lugar: v })} />
        {showMonto && (
          <div className="grid grid-cols-[1fr_110px] gap-2">
            <div><label className="label">{t("interaction.amountOpt")}</label><input data-testid="interaction-monto" type="number" min={0} step="0.01" className="field" value={f.monto} onChange={(e) => setF({ ...f, monto: e.target.value })} /></div>
            <div><label className="label">{t("interaction.currency")}</label><select data-testid="interaction-moneda" className="field" value={f.moneda} onChange={(e) => setF({ ...f, moneda: e.target.value })}>{MONEDAS.map((m) => <option key={m}>{m}</option>)}</select></div>
          </div>
        )}
        <div><label className="label">{t("common.notesEnc")}</label><textarea data-testid="interaction-notas" className="field min-h-[70px]" value={f.notas} onChange={(e) => setF({ ...f, notas: e.target.value })} /></div>
        <button data-testid="interaction-save-btn" className="btn-primary" disabled={saving}>{saving ? t("common.saving") : t("common.save")}</button>
      </form>
    </Sheet>
  );
}
